import React, { createContext, useEffect, useState } from "react";
import { HashRouter as Router, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import NavBar from "./NavBar";
import PromoPopup, { OPEN_PROMO_POPUP_EVENT } from "./components/PromoPopup";
import routes from "./routes";
import SiteFooter from "./components/SiteFooter";
import "bootstrap/dist/css/bootstrap.min.css";
import ScrollToTop from "./ScrollToTop";
import * as Constants from "./api/constants";
import { loadPersistedAddonMeta } from "./utils/bookingFlowStorage";
import { normalizeAddonMetaPayload } from "./utils/normalizeAddonMeta";
import { CmsProvider } from "./context/CmsContext";
import { SiteThemeProvider } from "./context/SiteThemeContext";
import { MD_UP_QUERY, useMediaQuery } from "./hooks/useMediaQuery";
import WelcomeOverlay from "./components/WelcomeOverlay";

export const Context = createContext();

/** Maintenance covers home only so QA can open #/booknow, #/admin, etc. */
function isPublicLandingPath(pathname) {
  const p = String(pathname || "/")
    .trim()
    .replace(/\/+$/, "");
  const norm = p === "" ? "/" : p;
  const lower = norm.toLowerCase();
  return lower === "/" || lower === "/home";
}

/** Syncs breakpoint to <html> for CSS hooks: .pb-viewport-mobile / .pb-viewport-desktop */
function ViewportClassSync() {
  const isMdUp = useMediaQuery(MD_UP_QUERY);
  useEffect(() => {
    const el = document.documentElement;
    el.classList.toggle("pb-viewport-desktop", isMdUp);
    el.classList.toggle("pb-viewport-mobile", !isMdUp);
    return () => {
      el.classList.remove("pb-viewport-desktop", "pb-viewport-mobile");
    };
  }, [isMdUp]);
  return null;
}

function BookingTrafficCapture() {
  const [params] = useSearchParams();
  useEffect(() => {
    try {
      let src = (params.get("src") || "").trim().slice(0, 128);
      if (!src) src = (params.get("utm_source") || "").trim().slice(0, 128);
      if (!src) src = (params.get("ref") || "").trim().slice(0, 128);
      if (!src) return;
      sessionStorage.setItem("pb_booking_traffic_source", src);
    } catch {
      /* ignore */
    }
  }, [params]);
  return null;
}

const AppLayout = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const [maintenance, setMaintenance] = useState({ enabled: false, message: "" });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(Constants.SITE_MAINTENANCE_MODE)
      .then((r) => {
        if (cancelled) return;
        const d = r?.data ?? {};
        setMaintenance({
          enabled: d.enabled === true,
          message: String(d.message || "").trim(),
        });
      })
      .catch(() => {
        /* leave site usable if settings fail */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const gateMaintenance =
    maintenance.enabled && !isAdminRoute && isPublicLandingPath(location.pathname);

  useEffect(() => {
    document.body.classList.toggle("pb-admin-route", isAdminRoute);
    return () => document.body.classList.remove("pb-admin-route");
  }, [isAdminRoute]);

  return (
    <div className={`pb-app-shell${isAdminRoute ? " pb-app-shell--admin" : ""}`}>
      <ViewportClassSync />
      <BookingTrafficCapture />
      <ScrollToTop>
        {!isAdminRoute ? (
          <NavBar maintenanceMode={gateMaintenance} />
        ) : (
          <NavBar />
        )}
        {!isAdminRoute && <PromoPopup maintenanceMode={gateMaintenance} />}
        <main
          className={`pb-site-main pb-site-theme-scope${isAdminRoute ? " pb-site-main--admin" : ""}`}
        >
          {gateMaintenance ? (
            <div
              className="flex min-h-[70vh] flex-col items-center justify-center bg-[#05020a] px-4 py-16 text-center text-white"
              role="alert"
            >
              <h1 className="mb-4 max-w-lg text-2xl font-black tracking-tight md:text-3xl">
                We&apos;re getting ready for something new
              </h1>
              <p className="max-w-xl text-sm leading-relaxed text-[#c9c1d9] md:text-base">
                {maintenance.message ||
                  "Our new website is launching soon — please check back shortly."}
              </p>
              <p className="mt-6 max-w-lg text-xs leading-relaxed text-[#9d94b0] md:text-sm">
                You can still unlock today&apos;s offer below — we&apos;ll use your details only to
                share the code and help with your booking.
              </p>
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent(OPEN_PROMO_POPUP_EVENT))
                }
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 px-6 py-3 text-sm font-black uppercase tracking-wide text-white shadow-lg transition hover:opacity-95"
              >
                <i className="bi bi-stars" aria-hidden />
                Get promo code
              </button>
            </div>
          ) : (
            <Routes>
              {routes.map((route, index) => (
                <Route
                  key={index}
                  path={route.path}
                  element={route.component}
                />
              ))}
            </Routes>
          )}
        </main>
      </ScrollToTop>
      {!isAdminRoute && !gateMaintenance && <SiteFooter />}
    </div>
  );
};

/**
 * Seeds the admin session bits from sessionStorage so a hard refresh inside the admin console
 * doesn't drop the user back to login (only the booking-only ADMIN sees the booking tab; the
 * server still gates SUPER_ADMIN endpoints by header).
 */
function readPersistedAdminSession() {
  try {
    const raw = sessionStorage.getItem("partyboss_admin_session");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      adminRole: String(parsed.role || "").toUpperCase() || undefined,
      adminUserName: parsed.userName || "",
    };
  } catch {
    return null;
  }
}

const App = () => {
  const seededAdmin = readPersistedAdminSession();
  const [context, setContext] = useState({
    eventDate: new Date().toISOString().split("T")[0],
    eventId: "",
    selectedSeats: 0,
    isAdminLoggedIn: Boolean(seededAdmin?.adminRole),
    adminRole: seededAdmin?.adminRole,
    adminUserName: seededAdmin?.adminUserName || "",
    bookingReferenceId: "",
  });
  const [selectedTheaterByDate, setselectedTheaterByDate] = useState({});
  const [eventDate, setEventDate] = useState({});
  const [theatersList, setTheatersList] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [addonMetaData, setAddonMetaData] = useState(() =>
    normalizeAddonMetaPayload(loadPersistedAddonMeta() ?? {})
  );
  const [selectedTime, SetSelectedTime] = useState([]);
  const [theatreGalleryByKey, setTheatreGalleryByKey] = useState({});
  const [theatreVideoByKey, setTheatreVideoByKey] = useState({});
  const [theatreExperienceByKey, setTheatreExperienceByKey] = useState({});
  const providerValue = React.useMemo(
    () => ({
      context,
      setContext,
      selectedTheaterByDate,
      setselectedTheaterByDate,
      eventDate,
      setEventDate,
      theatersList,
      setTheatersList,
      totalCost,
      setTotalCost,
      addonMetaData,
      setAddonMetaData,
      selectedTime,
      SetSelectedTime,
      theatreGalleryByKey,
      setTheatreGalleryByKey,
      theatreVideoByKey,
      setTheatreVideoByKey,
      theatreExperienceByKey,
      setTheatreExperienceByKey,
    }),
    [
      context,
      selectedTheaterByDate,
      eventDate,
      theatersList,
      addonMetaData,
      selectedTime,
      totalCost,
      theatreGalleryByKey,
      theatreVideoByKey,
      theatreExperienceByKey,
    ]
  );
  return (
    <Context.Provider value={providerValue}>
      <SiteThemeProvider>
        <CmsProvider>
          <Router>
            <>
              <WelcomeOverlay />
              <AppLayout />
            </>
          </Router>
        </CmsProvider>
      </SiteThemeProvider>
    </Context.Provider>
  );
};

export default App;
